import { ExternalDriverResponseDto } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ColoredLabel,
  ColorSchema,
  OptionsButton,
  TomTable,
  tomTableColumnFilterPresets,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { ExternalDriverViewStore } from '../../Stores/ExternalDriverViewStore'

interface IProps {
  store: ExternalDriverViewStore
  onEdit: (item: ExternalDriverResponseDto) => void
  onRemove: (item: ExternalDriverResponseDto) => void
  onRemoveBan: (item: ExternalDriverResponseDto) => void
  onBan: (item: ExternalDriverResponseDto) => void
}

export const ExternalDriverList: FC<IProps> = observer(
  ({ store, onEdit, onRemove, onRemoveBan, onBan }) => {
    const { t } = useTranslate()

    const columns = useMemo<MRT_ColumnDef<ExternalDriverResponseDto>[]>(
      () => [
        {
          accessorKey: 'driverID',
          header: t('idNumber', 'ID number'),
          ...tomTableColumnFilterPresets.searchMandatory(),
        },
        {
          accessorKey: 'name',
          header: t('name', 'Name'),
          ...tomTableColumnFilterPresets.searchMandatory(),
        },
        {
          accessorKey: 'notes',
          header: t('notes', 'Notes'),
          size: 300,
          muiTableBodyCellProps: {
            sx: {
              whiteSpace: 'normal',
            },
          },
          ...tomTableColumnFilterPresets.searchOptional(),
        },
        {
          accessorKey: 'banned',
          header: t('status', 'Status'),
          Cell: cell => (
            <ColoredLabel
              sx={{ display: 'inline-block' }}
              color={(cell.row.original.banned ? 'warning' : 'success') as ColorSchema}
              label={cell.row.original.banned ? t('banned', 'Banned') : t('active', 'Active')}
            />
          ),
          filterVariant: 'select',
          filterSelectOptions: [
            {
              label: t('banned', 'Banned'),
              value: 'true',
            },
            {
              label: t('active', 'Active'),
              value: 'false',
            },
          ],
          filterFn: 'equals',
          enableColumnFilterModes: false,
        },
      ],
      [t],
    )

    const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
      'planning/external-drivers',
    )

    const table = useTomTable(
      {
        columns,
        data: store.items,
        enableRowSelection: false,
        enableTopToolbar: false,
        enableExpanding: false,
        muiTableContainerProps: {
          sx: {
            maxHeight: 'calc(100vh - 220px)',
            minHeight: 'calc(100vh - 220px)',
          },
        },
        renderRowActions: ({ row }) => {
          const options: ButtonOption[] = [
            {
              label: t('edit', 'Edit'),
              onClick: () => onEdit(row.original),
              testId: 'edit-external-driver-option',
            },
            row.original.banned
              ? {
                  label: t('removeBan', 'Remove ban'),
                  onClick: () => onRemoveBan(row.original),
                  testId: 'remove-ban-external-driver-option',
                }
              : {
                  label: t('ban', 'Ban'),
                  onClick: () => onBan(row.original),
                  testId: 'ban-external-driver-option',
                },
            {
              label: t('delete', 'Delete'),
              onClick: () => onRemove(row.original),
              testId: 'delete-external-driver-option',
            },
          ]

          return (
            <OptionsButton
              tooltip={t('moreOptions', 'More options')}
              options={options}
              position='right'
              testId='external-driver-options-btn'
            />
          )
        },
      },
      undefined,
      undefined,
      { columns, savedPreferences, onPreferencesChange },
    )

    return <TomTable table={table} />
  },
)
